import React from 'react'
import { Box, CircularProgress, LinearProgress, Typography, keyframes, Collapse, Stack, Chip } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { UploadingFile, UploadStep } from '../hooks/useUpload'

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`

const slideIn = keyframes`
  from { transform: translateX(400px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`

type Props = {
  uploadingFiles: UploadingFile[]
}

const stepMessages: Record<UploadStep, string> = {
  idle: 'Concluído',
  creating: 'Preparando...',
  uploading: 'Enviando...',
  confirming: 'Finalizando...',
}

export default function UploadProgress({ uploadingFiles }: Props) {
  if (uploadingFiles.length === 0) return null

  const completedCount = uploadingFiles.filter(f => f.status === 'idle' && !f.error && !f.warning).length
  const warningCount = uploadingFiles.filter(f => f.warning).length
  const errorCount = uploadingFiles.filter(f => f.error).length
  const uploadingCount = uploadingFiles.filter(f => f.status !== 'idle' && !f.error && !f.warning).length

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 360,
        maxHeight: 'calc(100vh - 48px)',
        bgcolor: '#fff',
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1300,
        animation: `${slideIn} 0.3s ease-out`
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          flexShrink: 0,
          bgcolor: uploadingCount > 0 ? '#1976d2' : errorCount > 0 ? '#d32f2f' : warningCount > 0 && errorCount === 0 ? '#f59e0b' : '#4caf50',
          color: '#fff'
        }}
      >
        <CloudUploadIcon />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {uploadingCount > 0 
            ? `Enviando ${uploadingCount} arquivo(s)...`
            : errorCount > 0
              ? `${errorCount} erro(s)`
              : warningCount > 0
                ? `${warningCount} não elegível(is)`
                : 'Upload concluído!'}
        </Typography>
        {uploadingCount > 0 && (
          <Box sx={{ ml: 'auto' }}>
            <CircularProgress size={20} sx={{ color: '#fff' }} />
          </Box>
        )}
      </Box>

      {/* File list */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 2 }}>
        <Stack spacing={1.5}>
          {uploadingFiles.map((uf, index) => (
            <Box
              key={index}
              sx={{
                p: 1.5,
                borderRadius: 2,
                bgcolor: uf.error ? 'rgba(211, 47, 47, 0.05)' : uf.warning ? 'rgba(245, 158, 11, 0.05)' : uf.status === 'idle' ? 'rgba(76, 175, 80, 0.05)' : 'rgba(25, 118, 210, 0.05)',
                border: '1px solid',
                borderColor: uf.error ? '#d32f2f' : uf.warning ? '#f59e0b' : uf.status === 'idle' ? '#4caf50' : '#1976d2'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {uf.error ? (
                  <ErrorIcon sx={{ fontSize: 18, color: '#d32f2f' }} />
                ) : uf.warning ? (
                  <WarningAmberIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                ) : uf.status === 'idle' ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                ) : (
                  <CircularProgress size={18} sx={{ color: '#1976d2' }} />
                )}
                
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}
                >
                  {uf.file.name}
                </Typography>
                
                <Chip 
                  label={uf.error ? 'Erro' : uf.warning ? 'Não elegível' : stepMessages[uf.status]}
                  size="small"
                  sx={{ 
                    height: 20,
                    fontSize: '0.7rem',
                    bgcolor: uf.error ? 'rgba(211, 47, 47, 0.1)' : uf.warning ? 'rgba(245, 158, 11, 0.1)' : uf.status === 'idle' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                    color: uf.error ? '#d32f2f' : uf.warning ? '#f59e0b' : uf.status === 'idle' ? '#4caf50' : '#1976d2'
                  }}
                />
              </Box>

              {uf.error && (
                <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5 }}>
                  {uf.error}
                </Typography>
              )}

              {uf.warning && (
                <Typography variant="caption" sx={{ color: '#f59e0b', display: 'block', mt: 0.5 }}>
                  {uf.warning}
                </Typography>
              )}

              {!uf.error && uf.status !== 'idle' && (
                <LinearProgress
                  variant="determinate"
                  value={uf.progress}
                  sx={{
                    mt: 1,
                    height: 4,
                    borderRadius: 2,
                    bgcolor: 'rgba(0,0,0,0.05)',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2
                    }
                  }}
                />
              )}
            </Box>
          ))}
        </Stack>
      </Box>
    </Box>
  )
}
